import { useState, useCallback, useRef } from "react";

const useEditConflictDetection = () => {
  const [editingStates, setEditingStates] = useState({}); // Track what's being edited
  const [conflicts, setConflicts] = useState([]); // Track detected conflicts
  const editingDataRef = useRef({}); // Store current editing data for comparison

  // Mark an entity as being edited
  const startEditing = useCallback((entityType, entityId, currentData) => {
    const key = `${entityType}_${entityId}`;
    setEditingStates((prev) => ({
      ...prev,
      [key]: true,
    }));
    editingDataRef.current[key] = {
      ...currentData,
      _editStartTime: Date.now(),
    };
  }, []);

  // Mark an entity as no longer being edited
  const stopEditing = useCallback((entityType, entityId) => {
    const key = `${entityType}_${entityId}`;
    setEditingStates((prev) => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
    delete editingDataRef.current[key];

    // Remove any conflicts for this entity
    setConflicts((prev) => prev.filter((conflict) => conflict.key !== key));
  }, []);

  // Check if an entity is currently being edited
  const isEditing = useCallback(
    (entityType, entityId) => {
      const key = `${entityType}_${entityId}`;
      return editingStates[key] || false;
    },
    [editingStates],
  );

  // Detect conflicts when SSE update comes in
  const checkForConflicts = useCallback(
    (updateType, updateData) => {
      const { entityType, entityId, newData, updateTimestamp } = updateData;
      const key = `${entityType}_${entityId}`;

      // Check if user is currently editing this entity
      if (editingStates[key]) {
        const currentEditData = editingDataRef.current[key];

        // Check if the update happened after editing started
        const editStartTime = currentEditData?._editStartTime || 0;
        const updateTime = updateTimestamp || Date.now();

        if (updateTime > editStartTime) {
          // Detect specific field conflicts
          const conflictedFields = [];
          if (currentEditData && newData) {
            Object.keys(newData).forEach((field) => {
              if (
                field !== "_editStartTime" &&
                currentEditData[field] !== undefined &&
                currentEditData[field] !== newData[field]
              ) {
                conflictedFields.push(field);
              }
            });
          }

          if (conflictedFields.length > 0) {
            const conflict = {
              key,
              entityType,
              entityId,
              updateType,
              conflictedFields,
              serverData: newData,
              clientData: currentEditData,
              timestamp: Date.now(),
              id: `${key}_${Date.now()}`,
            };

            setConflicts((prev) => {
              // Remove any existing conflicts for this entity and add new one
              const filtered = prev.filter((c) => c.key !== key);
              return [...filtered, conflict];
            });

            return conflict;
          }
        }
      }

      return null;
    },
    [editingStates],
  );

  // Resolve a conflict (user chooses server or client data)
  const resolveConflict = useCallback(
    (conflictId, resolution, resolvedData = null) => {
      setConflicts((prev) => {
        const conflict = prev.find((c) => c.id === conflictId);
        if (!conflict) return prev;

        // Update the editing data based on resolution
        if (resolution === "accept_server") {
          editingDataRef.current[conflict.key] = {
            ...conflict.serverData,
            _editStartTime: Date.now(), // Reset edit time
          };
        } else if (resolution === "keep_client") {
          // Keep current editing data, just remove conflict
        } else if (resolution === "merge" && resolvedData) {
          editingDataRef.current[conflict.key] = {
            ...resolvedData,
            _editStartTime: Date.now(),
          };
        }

        return prev.filter((c) => c.id !== conflictId);
      });
    },
    [],
  );

  // Get all current conflicts
  const getConflicts = useCallback(() => conflicts, [conflicts]);

  // Get conflicts for a specific entity
  const getConflictsForEntity = useCallback(
    (entityType, entityId) => {
      const key = `${entityType}_${entityId}`;
      return conflicts.filter((c) => c.key === key);
    },
    [conflicts],
  );

  return {
    startEditing,
    stopEditing,
    isEditing,
    checkForConflicts,
    resolveConflict,
    getConflicts,
    getConflictsForEntity,
    editingStates,
    conflicts,
  };
};

export default useEditConflictDetection;
